import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { announcementAPI } from '../../services/api';
import { FaBullhorn, FaClock, FaArrowRight } from 'react-icons/fa';
import {
    DashboardHeader,
    LoadingSpinner,
    EmptyState
} from '../../components/dashboard';

const Announcements = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadAnnouncements();
    }, []);

    const loadAnnouncements = async () => {
        try {
            const response = await announcementAPI.getMyFeed();
            setAnnouncements(response.announcements || []);
        } catch (err) {
            console.error('Failed to load announcements:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatTimeAgo = (dateString) => {
        if (!dateString) return 'Recently';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
        if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
        if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
        return date.toLocaleDateString();
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-6 py-8">
                <DashboardHeader
                    icon={FaBullhorn}
                    title="Announcements"
                    subtitle="Stay updated with the latest news and updates"
                />

                {announcements.length === 0 ? (
                    <EmptyState
                        icon={FaBullhorn}
                        message="No announcements at the moment."
                    />
                ) : (
                    <div className="space-y-4">
                        {announcements.map((announcement) => (
                            <Link
                                key={announcement.id}
                                to={`/announcements/${announcement.id}`}
                                className="block bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-start gap-4">
                                    <div className="p-3 bg-blue-50 rounded-lg">
                                        <FaBullhorn className="text-xl text-blue-600" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <h3 className="text-xl font-bold text-gray-900">{announcement.title}</h3>
                                            {announcement.is_pinned && (
                                                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-bold rounded">
                                                    PINNED
                                                </span>
                                            )}
                                            {announcement.type === 'urgent' && (
                                                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded">
                                                    URGENT
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-gray-700 mb-3 line-clamp-3">{announcement.content}</p>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                                <FaClock className="text-xs" />
                                                <span>
                                                    {announcement.created_by_name || announcement.created_by_username || 'Admin'} • {formatTimeAgo(announcement.created_at)}
                                                </span>
                                            </div>
                                            <span className="text-blue-600 text-sm font-medium">
                                                Read More <FaArrowRight className="inline ml-1" />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Announcements;
